// DeepSeek API service for AI Drill generation
const DEEPSEEK_API_KEY = 'sk-04a8bc1279a0475eaab02f745561042b';
const DEEPSEEK_API_URL = 'https://api.deepseek.com/chat/completions';

class DeepSeekDrillApiService {
  constructor() {
    this.apiKey = DEEPSEEK_API_KEY;
    this.baseUrl = DEEPSEEK_API_URL;
  }

  async generateDrillQuestions(originalQuestion, questionType, difficulty = 'primary', grade = 'p1') {
    try {
      const prompt = this.createDrillPrompt(originalQuestion, questionType, difficulty, grade);
      
      const startTime = Date.now();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        signal: controller.signal,
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: 'You are an expert Chinese language teacher specializing in creating educational drill questions for primary school students. You must ALWAYS respond with ONLY valid JSON format. Do not include any explanatory text, markdown formatting, or additional comments. Your response must be pure JSON that can be parsed directly.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.3,
          max_tokens: 2000,
        }),
      });

      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('DeepSeek Drill API Error Response:', errorText);
        throw new Error(`DeepSeek API error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('DeepSeek Drill API Response:', data);
      
      if (!data.choices || !data.choices[0] || !data.choices[0].message) {
        throw new Error('Invalid API response structure');
      }
      
      const content = data.choices[0].message.content;
      console.log('Raw content from API:', content);
      
      // Parse the JSON response with better error handling
      let drillData;
      try {
        // Try to extract JSON from the response if it contains extra text
        let jsonContent = content.trim();
        
        // Find JSON object in the response
        const jsonMatch = jsonContent.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          jsonContent = jsonMatch[0];
        }
        
        drillData = JSON.parse(jsonContent);
        
        // Validate the structure
        if (!drillData.drillQuestions || !Array.isArray(drillData.drillQuestions)) {
          throw new Error('Invalid drill data structure');
        }
        
      } catch (parseError) {
        console.error('JSON Parse Error:', parseError);
        console.error('Content that failed to parse:', content);
        
        // Create fallback drill data
        drillData = {
          drillQuestions: [
            {
              question: "選擇正確的量詞：一（ ）書",
              options: ["本", "個", "張", "支"],
              correctAnswer: "本",
              explanation: "『本』是用來計算書本的量詞。"
            },
            {
              question: "選擇正確的量詞：一（ ）花",
              options: ["朵", "枝", "束", "盆"],
              correctAnswer: "朵",
              explanation: "『朵』是用來計算花朵的量詞。"
            },
            {
              question: "選擇正確的量詞：一（ ）筆",
              options: ["支", "根", "枝", "條"],
              correctAnswer: "支",
              explanation: "『支』是用來計算筆的量詞。"
            },
            {
              question: "選擇正確的量詞：一（ ）桌子",
              options: ["張", "個", "把", "台"],
              correctAnswer: "張",
              explanation: "『張』是用來計算桌子的量詞。"
            },
            {
              question: "選擇正確的量詞：一（ ）椅子",
              options: ["把", "張", "個", "條"],
              correctAnswer: "把",
              explanation: "『把』是用來計算椅子的量詞。"
            }
          ],
          questionType: questionType,
          difficulty: difficulty,
          grade: grade
        };
        
        console.log('Using fallback drill data due to parsing error');
      }

      const endTime = Date.now();
      const timeElapsed = Math.floor((endTime - startTime) / 1000);
      
      // Calculate token usage and cost
      const inputTokens = data.usage?.prompt_tokens || 0;
      const outputTokens = data.usage?.completion_tokens || 0;
      const totalTokens = data.usage?.total_tokens || 0;
      
      const cost = this.calculateCost(inputTokens, outputTokens);
      
      return {
        ...drillData,
        metadata: {
          timeElapsed,
          inputTokens,
          outputTokens,
          totalTokens,
          cost,
          originalQuestion
        }
      };
    } catch (error) {
      console.error('Error generating drill questions:', error);
      
      if (error.name === 'AbortError') {
        throw new Error('API request timed out. Please try again.');
      }
      
      throw error;
    }
  }

  createDrillPrompt(originalQuestion, questionType, difficulty, grade) {
    const gradeMap = {
      'p1': '小學一年級',
      'p2': '小學二年級', 
      'p3': '小學三年級',
      'p4': '小學四年級',
      'p5': '小學五年級',
      'p6': '小學六年級'
    };

    return `你是一個專業的中文教師，專門為小學學生創建練習題。請根據以下要求生成5道相似的練習題：

原始題目：${originalQuestion}
題目類型：${questionType}
適合年級：${gradeMap[grade]}
難度等級：${difficulty}

要求：
1. 生成5道相似的練習題，保持相同的題目結構和難度
2. 每道題目都要有正確答案和詳細解釋
3. 題目應該適合${gradeMap[grade]}學生的認知水平
4. 保持相同的語法結構和詞彙難度
5. 提供4個選項，其中只有1個是正確答案

請嚴格按照以下JSON格式回應，不要添加任何其他文字或說明：

{
  "drillQuestions": [
    {
      "question": "選擇正確的量詞：一（ ）書",
      "options": ["本", "個", "張", "支"],
      "correctAnswer": "本",
      "explanation": "『本』是用來計算書本的量詞。"
    },
    {
      "question": "選擇正確的量詞：一（ ）花",
      "options": ["朵", "枝", "束", "盆"],
      "correctAnswer": "朵",
      "explanation": "『朵』是用來計算花朵的量詞。"
    }
  ],
  "questionType": "${questionType}",
  "difficulty": "${difficulty}",
  "grade": "${grade}"
}

請確保回應是有效的JSON格式，不要包含任何額外的文字、標點符號或說明。`;
  }

  calculateCost(inputTokens, outputTokens) {
    // DeepSeek API pricing (in RMB per million tokens)
    const inputPrice = 2.0; // 標準時段價格，輸入tokens
    const outputPrice = 8.0; // 標準時段價格，輸出tokens
    
    const inputCost = (inputTokens / 1000000) * inputPrice;
    const outputCost = (outputTokens / 1000000) * outputPrice;
    
    return {
      inputCost: parseFloat(inputCost.toFixed(6)),
      outputCost: parseFloat(outputCost.toFixed(6)),
      totalCost: parseFloat((inputCost + outputCost).toFixed(6))
    };
  }

  // Get current time in Beijing timezone to determine if it's discount period
  isDiscountPeriod() {
    const now = new Date();
    const beijingTime = new Date(now.getTime() + (8 * 60 * 60 * 1000)); // UTC+8
    const hour = beijingTime.getUTCHours();
    
    // Discount period: 00:30-08:30 Beijing time
    return hour >= 0 && hour < 8;
  }
}

export default new DeepSeekDrillApiService(); 